'use client';

import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ResponsivePopup,
  ResponsivePopupContent,
  ResponsivePopupTrigger,
} from '@kit/ui/responsive-popup';
import { cn } from '@kit/ui/utils';

export type LegendSeverity = 'critical' | 'important' | 'moderate' | 'minor';

const LEGEND_ORDER: LegendSeverity[] = [
  'critical',
  'important',
  'moderate',
  'minor',
];

/**
 * User-facing description of each severity level lives in the `interactions`
 * namespace under `severity.{level}.{label,short,long}`. The wording is
 * deliberately softer than the LLM prompt's criteria: levels of attention on
 * a continuum, not rules.
 */

const legendColors: Record<LegendSeverity, { dot: string; text: string }> = {
  critical: { dot: 'bg-red-500', text: 'text-red-700 dark:text-red-400' },
  important: {
    dot: 'bg-orange-500',
    text: 'text-orange-700 dark:text-orange-400',
  },
  moderate: {
    dot: 'bg-yellow-500',
    text: 'text-yellow-700 dark:text-yellow-400',
  },
  minor: { dot: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-400' },
};

function SeverityLegendContent() {
  const { t } = useTranslation('interactions');

  return (
    <div>
      {/* Key framing first: what the category is (and is not) */}
      <div className="border-b border-zinc-200 pb-3 dark:border-zinc-700">
        <p className="border-l-4 border-zinc-300 pl-3 text-xs leading-relaxed text-zinc-600 italic dark:border-zinc-600 dark:text-zinc-300">
          <span className="font-medium text-zinc-800 dark:text-zinc-100">
            {t('legend.disclaimerLead')}
          </span>{' '}
          {t('legend.disclaimerRest')}
        </p>
      </div>
      <ul className="mt-3 space-y-3">
        {LEGEND_ORDER.map((sev) => (
          <li
            key={sev}
            className="grid grid-cols-[8px_1fr] gap-x-2.5 gap-y-0.5"
          >
            <span className="flex h-5 items-center">
              <span
                className={cn('h-2 w-2 rounded-full', legendColors[sev].dot)}
              />
            </span>
            <p className="text-[13px] leading-5">
              <span className={cn('font-semibold', legendColors[sev].text)}>
                {t(`severity.${sev}.label`)}
              </span>
              <span className="text-zinc-400"> · </span>
              <span className="font-medium text-zinc-800 dark:text-zinc-100">
                {t(`severity.${sev}.short`)}
              </span>
            </p>
            <p className="col-start-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
              {t(`severity.${sev}.long`)}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * "ⓘ legenda" link opening the full severity legend.
 *
 * Desktop/tablet: popover anchored to the link. Phone: bottom sheet fixed
 * to the viewport, because an anchored popover taller than the screen
 * flips sides and jitters while the page scrolls.
 */
export function SeverityLegendLink() {
  const { t } = useTranslation('interactions');

  return (
    <ResponsivePopup desktop="popover">
      <ResponsivePopupTrigger asChild>
        <button
          type="button"
          data-test="severity-legend-trigger"
          className="inline-flex items-center gap-1 text-xs text-zinc-500 underline decoration-zinc-300 decoration-dotted underline-offset-2 transition-colors hover:text-zinc-700 data-[state=open]:text-zinc-700 dark:text-zinc-400 dark:decoration-zinc-600 dark:hover:text-zinc-200 dark:data-[state=open]:text-zinc-200"
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
          {t('legend.link')}
        </button>
      </ResponsivePopupTrigger>
      <ResponsivePopupContent
        title={t('legend.title')}
        titleClassName="text-sm font-semibold text-zinc-800 dark:text-zinc-100"
        popoverProps={{ align: 'end', sideOffset: 8, collisionPadding: 16 }}
        desktopClassName="w-[380px] max-w-[calc(100vw-32px)] rounded-lg border-2 border-zinc-200 p-5 shadow-[3px_3px_0_0_#e4e4e7] dark:border-zinc-700 dark:shadow-[3px_3px_0_0_#3f3f46]"
      >
        <SeverityLegendContent />
      </ResponsivePopupContent>
    </ResponsivePopup>
  );
}
